import { z } from "zod";
/*
 * Workspace change feed (decision 0002). Thin events: resource identity plus
 * operation, never payloads — consumers refetch the resource by id. `seq` is
 * a per-tenant monotonic cursor; holding the highest seen seq is the only
 * client-side sync state required. Each reader sees only the events about
 * what they may read.
 *
 * Following it: pass the last page's `cursor` as `afterSeq` until `isDone`,
 * then keep that cursor. The feed keeps its events, so a cursor at or below
 * `tip` continues where it stopped. A cursor that is too old to continue from
 * (above `tip`, because the workspace was restored; or a cache the client has
 * lost or no longer trusts) means reading everything again: note `tip` first,
 * refetch every resource the cache holds, then follow the events after that
 * `tip`, so nothing written during the re-read is missed.
 */
/**
 * Every resource the feed names, as `workspace_events_resource_type_check`
 * allows: the control plane emits through this type, so a new resource type
 * is added here, in the same change as the migration that allows it.
 */
export const workspaceResourceTypeSchema = z.enum([
    "channel",
    "channel_post",
    "channel_thread",
    "object_type",
    "field",
    "record",
    "view",
    "file",
    "memory",
    "inbox_item",
    "task",
    "session",
    "navigation_pin",
    "channel_group",
    "channel_pin",
    "plugin_connection",
    "connector_job",
    "twin_batch",
    "twin_proposal",
    "twin_attention",
    "billing_notice",
    "automation",
    /** A share (docs/specs/twin-shares.md): its events reach only its participants. */
    "share",
    /** A shared mailbox's conversation, its assignee and status (section 3): its events reach only the mailbox's readers. */
    "mailbox_conversation",
]);
export const workspaceEventOperationSchema = z.enum(["upsert", "delete"]);
export const workspaceEventSchema = z.object({
    seq: z.number().int().positive(),
    organisationId: z.string().trim().min(1).optional(),
    resourceType: workspaceResourceTypeSchema,
    resourceId: z.string().trim().min(1),
    channelId: z.string().trim().min(1).optional(),
    operation: workspaceEventOperationSchema,
    createdAt: z.number().int().nonnegative(),
});
export const listWorkspaceEventsQuerySchema = z.object({
    afterSeq: z.coerce.number().int().nonnegative().optional(),
    limit: z.coerce.number().int().min(1).max(500).default(500),
});
/**
 * The public read of the feed (twin.workspace-events.list). With `waitSeconds` (at most 10), a read that finds nothing
 * new waits up to that long for the next change the person may read and answers as soon as one lands.
 */
export const twinWorkspaceEventsQuerySchema = listWorkspaceEventsQuerySchema.extend({
    waitSeconds: z.coerce.number().int().min(0).max(10).optional(),
});
export const listWorkspaceEventsResponseSchema = z.object({
    events: z.array(workspaceEventSchema),
    cursor: z.number().int().nonnegative(),
    isDone: z.boolean(),
    /** Current end of the tenant's feed — read it before a snapshot, then follow deltas from it. */
    tip: z.number().int().nonnegative(),
});
/*
 * Usage metering (plan 005): tenant-level daily metric snapshots.
 */
export const usageSnapshotsQuerySchema = z.object({
    from: z.coerce.number().int().nonnegative(),
    to: z.coerce.number().int().nonnegative(),
    metricKey: z.string().trim().min(1).max(120).optional(),
});
export const usageSnapshotSchema = z.object({
    metricKey: z.string(),
    periodStart: z.number().int().nonnegative(),
    value: z.number().int().nonnegative(),
});
